import type {
    IWasteMovementDefaultFieldSaveItem,
    IWasteMovementDefaultsPageModel,
    IWasteMovementEwcDefaultRule,
    IWasteMovementEwcRuleSavePayload,
} from "@/customTypings/wasteMovement/WasteMovementDefaults";
import { wasteMovementDefaultsService } from "@/services/wasteMovementDefaults/wasteMovementDefaults.service";

export type EwcRuleDraft = IWasteMovementEwcDefaultRule & {
    isNew: boolean;
};

export function buildLookupDisplayItem(item: { code: string; description: string }): string {
    const code = item.code.trim();
    const description = item.description.trim();
    if (code && description) return `${code} — ${description}`;
    return code || description;
}

export function applyPageModelToEwcRuleDrafts(rules: ReadonlyArray<IWasteMovementEwcDefaultRule>): EwcRuleDraft[] {
    return rules.map((rule) => ({ ...rule, isNew: false }));
}

export function buildEwcRuleDraftFromModal(
    existing: EwcRuleDraft | null,
    triggerEwcIds: string[],
    targetValues: Record<string, string>,
    ewcs: ReadonlyArray<{ id: string; code: string; description: string }>,
    lookupItemsForField: (fieldKey: string) => ReadonlyArray<{ id: string; code: string; description: string }>
): EwcRuleDraft {
    const id = existing?.id ?? crypto.randomUUID();
    const filteredTargets = Object.fromEntries(Object.entries(targetValues).filter(([, valueGuid]) => valueGuid));

    const triggerEwcDisplays = triggerEwcIds.map((ewcId) => {
        const ewc = ewcs.find((item) => item.id === ewcId);
        return ewc ? buildLookupDisplayItem(ewc) : ewcId;
    });

    const targetDisplays: Record<string, string> = {};
    for (const [fieldKey, valueGuid] of Object.entries(filteredTargets)) {
        const item = lookupItemsForField(fieldKey).find((lookupItem) => lookupItem.id === valueGuid);
        targetDisplays[fieldKey] = item ? buildLookupDisplayItem(item) : valueGuid;
    }

    return {
        id,
        isNew: existing?.isNew ?? !existing,
        isEnabled: existing?.isEnabled ?? true,
        isDeleted: false,
        triggerEwcIds,
        triggerEwcDisplays,
        targetValues: filteredTargets,
        targetDisplays,
    };
}

export function applyEwcRuleDraftAction(
    drafts: ReadonlyArray<EwcRuleDraft>,
    ruleId: string,
    action: "enable" | "disable" | "softdelete"
): EwcRuleDraft[] {
    if (action === "softdelete") {
        const rule = drafts.find((draft) => draft.id === ruleId);
        if (rule?.isNew) return drafts.filter((draft) => draft.id !== ruleId);
        return drafts.map((draft) =>
            draft.id === ruleId ? { ...draft, isDeleted: true, isEnabled: false } : draft
        );
    }

    return drafts.map((draft) => {
        if (draft.id !== ruleId) return draft;
        return { ...draft, isEnabled: action === "enable" };
    });
}

export function upsertEwcRuleDraft(drafts: ReadonlyArray<EwcRuleDraft>, draft: EwcRuleDraft): EwcRuleDraft[] {
    const index = drafts.findIndex((item) => item.id === draft.id);
    if (index < 0) return [...drafts, draft];
    return drafts.map((item, itemIndex) => (itemIndex === index ? draft : item));
}

/** EWC codes already assigned to other enabled, non-deleted rules (matches server overlap validation). */
export function getEwcIdsUsedByOtherEnabledRules(
    drafts: ReadonlyArray<EwcRuleDraft>,
    excludeRuleId?: string | null
): ReadonlySet<string> {
    const used = new Set<string>();
    for (const rule of drafts) {
        if (rule.isDeleted || !rule.isEnabled) continue;
        if (excludeRuleId && rule.id === excludeRuleId) continue;
        for (const ewcId of rule.triggerEwcIds) {
            used.add(ewcId);
        }
    }
    return used;
}

export function getAvailableEwcOptionsForRuleModal<T extends { id: string }>(
    allEwcs: ReadonlyArray<T>,
    drafts: ReadonlyArray<EwcRuleDraft>,
    selectedEwcIds: ReadonlyArray<string>,
    editingRuleId?: string | null
): T[] {
    const usedByOtherRules = getEwcIdsUsedByOtherEnabledRules(drafts, editingRuleId);
    return allEwcs.filter(
        (ewc) => !usedByOtherRules.has(ewc.id) && !selectedEwcIds.includes(ewc.id)
    );
}

function sameStringArray(left: string[], right: string[]): boolean {
    if (left.length !== right.length) return false;
    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();
    return sortedLeft.every((value, index) => value === sortedRight[index]);
}

function sameTargetValues(left: Record<string, string>, right: Record<string, string>): boolean {
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(right).sort();
    if (!sameStringArray(leftKeys, rightKeys)) return false;
    return leftKeys.every((key) => left[key] === right[key]);
}

export function hasEwcRuleContentChanged(
    saved: IWasteMovementEwcDefaultRule,
    draft: IWasteMovementEwcDefaultRule
): boolean {
    return (
        !sameStringArray(saved.triggerEwcIds, draft.triggerEwcIds) ||
        !sameTargetValues(saved.targetValues, draft.targetValues)
    );
}

export type WasteMovementGeneralDefaultDraft = {
    fieldKey: string;
    mode: string;
    specificGuidValue: string;
};

/** True when general defaults or EWC rule drafts differ from the last loaded page model. */
export function hasUnsavedWasteMovementDefaultsChanges(
    generalDrafts: ReadonlyArray<WasteMovementGeneralDefaultDraft>,
    ewcRuleDrafts: ReadonlyArray<EwcRuleDraft>,
    pageModel: IWasteMovementDefaultsPageModel | null
): boolean {
    if (!pageModel) return false;

    const savedGeneralByKey = new Map(pageModel.generalDefaults.map((field) => [field.fieldKey, field]));
    for (const draft of generalDrafts) {
        const saved = savedGeneralByKey.get(draft.fieldKey);
        if (!saved) continue;
        if (saved.mode !== draft.mode) return true;
        const savedSpecific = saved.specificGuidValue ?? "";
        const draftSpecific = draft.mode === "SpecificValue" ? draft.specificGuidValue || "" : "";
        if (savedSpecific !== draftSpecific) return true;
    }

    const draftById = new Map(ewcRuleDrafts.map((rule) => [rule.id, rule]));

    for (const draft of ewcRuleDrafts) {
        if (draft.isNew && !draft.isDeleted) return true;
    }

    for (const saved of pageModel.ewcRules) {
        const draft = draftById.get(saved.id);
        if (!draft || draft.isDeleted) {
            if (!saved.isDeleted) return true;
            continue;
        }
        if (saved.isDeleted && !draft.isDeleted) return true;
        if (saved.isEnabled !== draft.isEnabled) return true;
        if (hasEwcRuleContentChanged(saved, draft)) return true;
    }

    return false;
}

function toEwcRulePayload(draft: EwcRuleDraft, movementDirection: string): IWasteMovementEwcRuleSavePayload {
    return {
        id: draft.isNew ? null : draft.id,
        movementDirection,
        triggerEwcIds: draft.triggerEwcIds,
        targetFields: Object.entries(draft.targetValues).map(([fieldKey, valueGuid]) => ({
            fieldKey,
            valueGuid,
        })),
    };
}

export async function persistWasteMovementDefaults(
    siteId: string,
    movementDirection: string,
    generalFields: IWasteMovementDefaultFieldSaveItem[],
    ewcDrafts: ReadonlyArray<EwcRuleDraft>,
    savedEwcRules: ReadonlyArray<IWasteMovementEwcDefaultRule>
): Promise<IWasteMovementDefaultsPageModel> {
    let model = await wasteMovementDefaultsService.saveGeneral(siteId, movementDirection, generalFields);

    const savedById = new Map(savedEwcRules.map((rule) => [rule.id, rule]));
    const draftById = new Map(ewcDrafts.map((rule) => [rule.id, rule]));

    for (const saved of savedEwcRules) {
        if (saved.isDeleted) continue;
        const draft = draftById.get(saved.id);
        if (!draft || draft.isDeleted) {
            model = await wasteMovementDefaultsService.softDeleteEwcRule(siteId, saved.id);
        }
    }

    for (const draft of ewcDrafts) {
        if (draft.isNew || draft.isDeleted) continue;
        const saved = savedById.get(draft.id);
        if (saved?.isDeleted) {
            model = await wasteMovementDefaultsService.restoreEwcRule(siteId, draft.id);
        }
    }

    for (const draft of ewcDrafts) {
        if (!draft.isNew || draft.isDeleted) continue;
        model = await wasteMovementDefaultsService.createEwcRule(
            siteId,
            toEwcRulePayload(draft, movementDirection)
        );
    }

    for (const draft of ewcDrafts) {
        if (draft.isNew || draft.isDeleted) continue;
        const saved = savedById.get(draft.id);
        if (!saved) continue;

        if (hasEwcRuleContentChanged(saved, draft)) {
            model = await wasteMovementDefaultsService.updateEwcRule(
                siteId,
                toEwcRulePayload(draft, movementDirection)
            );
        }

        if (saved.isEnabled !== draft.isEnabled) {
            model = draft.isEnabled
                ? await wasteMovementDefaultsService.enableEwcRule(siteId, draft.id)
                : await wasteMovementDefaultsService.disableEwcRule(siteId, draft.id);
        }
    }

    return model;
}
